/**
 * useCacheSync Hook
 * 
 * Initializes real-time cache synchronization via WebSocket.
 * Automatically connects/disconnects based on authentication state.
 * 
 * Features:
 * - Auto-connects when user is authenticated
 * - Auto-disconnects on logout
 * - Reconnects on auth token changes
 * - Provides connection status
 * 
 * Usage in App.tsx:
 * ```tsx
 * function App() {
 *   const queryClient = new QueryClient();
 *   useCacheSync(queryClient);
 *   
 *   return (
 *     <QueryClientProvider client={queryClient}>
 *       <YourApp />
 *     </QueryClientProvider>
 *   );
 * }
 * ```
 */

import { useEffect, useState, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { CacheEventListener } from '@/lib/cacheEventListener';
import { useAuth } from '@/hooks/useAuth';
import { getAuthToken } from '@/lib/authToken';

export interface CacheSyncStatus {
  isConnected: boolean;
  isEnabled: boolean;
  lastSync: number | null;
}

export function useCacheSync(queryClient: QueryClient): CacheSyncStatus {
  const [isConnected, setIsConnected] = useState(false);
  const listenerRef = useRef<CacheEventListener | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Re-connect Socket.IO whenever the logged-in user changes (login / logout / invite accept)
  const { user } = useAuth();

  useEffect(() => {
    const token = getAuthToken();

    if (!token || !user) {
      // No authenticated user — ensure we are disconnected
      if (listenerRef.current) {
        listenerRef.current.disconnect();
        listenerRef.current = null;
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    // Disconnect any pre-existing connection (handles user switch / re-login)
    if (listenerRef.current) {
      listenerRef.current.disconnect();
      listenerRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    // Initialize cache listener with the current auth token
    console.log('🚀 Initializing real-time cache sync for user:', user.id);
    listenerRef.current = new CacheEventListener(queryClient);
    listenerRef.current.connect();

    // Update connection status periodically
    checkIntervalRef.current = setInterval(() => {
      if (listenerRef.current) {
        setIsConnected(listenerRef.current.connected());
      }
    }, 2000);

    // Initial status check
    setTimeout(() => {
      if (listenerRef.current) {
        setIsConnected(listenerRef.current.connected());
      }
    }, 1000);

    // Cleanup on unmount or user change
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (listenerRef.current) {
        listenerRef.current.disconnect();
        listenerRef.current = null;
      }
      setIsConnected(false);
    };
  // Re-run whenever the authenticated user ID changes (covers login, logout, invite accept)
  }, [queryClient, user?.id]);

  // Listen for auth changes (logout, token refresh, etc.)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token') {
        // Token changed - reconnect
        if (e.newValue && listenerRef.current) {
          console.log('🔄 Auth token changed - reconnecting cache sync...');
          listenerRef.current.disconnect();
          setTimeout(() => {
            if (listenerRef.current) {
              listenerRef.current.connect();
            }
          }, 500);
        } else if (!e.newValue && listenerRef.current) {
          // Token removed - disconnect
          console.log('🔒 User logged out - disconnecting cache sync');
          listenerRef.current.disconnect();
          setIsConnected(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return {
    isConnected,
    isEnabled: true,
    lastSync: Date.now(),
  };
}
